import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const features = [
  {
    title: 'Smart Tokenization',
    description: 'Automatically breaks Japanese text into tokens using Kuromoji morphological analysis, handling verb conjugations and compound words.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
      </svg>
    ),
  },
  {
    title: 'Dictionary Lookup',
    description: 'Instant definitions from the Jitendex dictionary. Click any word to see its reading, meaning, and dictionary form.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
      </svg>
    ),
  },
  {
    title: 'Deck System',
    description: 'Organize vocabulary into custom decks like "Business", "JLPT N3", or "Tech". Save words while reading and review them later.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.429 9.75L2.25 12l4.179 2.25m0-4.5l5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L12 12.75 6.429 9.75m11.142 0l4.179 2.25-9.75 5.25-9.75-5.25 4.179-2.25" />
      </svg>
    ),
  },
  {
    title: 'Flashcard Review',
    description: 'Review saved vocabulary with interactive flashcards. Flip to reveal readings and meanings, track your progress through each deck.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.098 19.902a3.75 3.75 0 005.304 0l6.401-6.402M6.75 21A3.75 3.75 0 013 17.25V4.125C3 3.504 3.504 3 4.125 3h5.25c.621 0 1.125.504 1.125 1.125v4.072M6.75 21a3.75 3.75 0 003.75-3.75V8.197M6.75 21h13.125c.621 0 1.125-.504 1.125-1.125v-5.25c0-.621-.504-1.125-1.125-1.125h-4.072M10.5 8.197l2.88-2.88c.438-.439 1.15-.439 1.59 0l3.712 3.713c.44.44.44 1.152 0 1.59l-2.879 2.88M6.75 17.25h.008v.008H6.75v-.008z" />
      </svg>
    ),
  },
  {
    title: 'Anki Export',
    description: 'Export any deck as an Anki .apkg file for offline spaced-repetition study. Compatible with Anki desktop and mobile.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
      </svg>
    ),
  },
  {
    title: 'Furigana Support',
    description: 'Toggle furigana readings above kanji characters for easier reading. Powered by react-furi with accurate reading alignment.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 21l5.25-11.25L21 21m-9-3h7.5M3 5.621a48.474 48.474 0 016-.371m0 0c1.12 0 2.233.038 3.334.114M9 5.25V3m3.334 2.364C11.176 10.658 7.69 15.08 3 17.502m9.334-12.138c.896.061 1.785.147 2.666.257m-4.589 8.495a18.023 18.023 0 01-3.827-5.802" />
      </svg>
    ),
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-x-hidden">
      <style>{`
        @keyframes fade-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-up {
          animation: fade-up 0.5s ease-out both;
        }
      `}</style>

      {/* Top bar */}
      <header className="max-w-5xl mx-auto px-6 pt-6 flex justify-between items-center">
        <Link to="/" className="text-lg font-bold text-white" aria-label="Yomitool home">
          <span className="text-primary-400">Yomi</span>
          <span className="text-slate-400 font-light">tool</span>
        </Link>
        <nav>
          {user ? (
            <Link to="/app" className="text-sm text-slate-400 hover:text-white transition-colors">
              Go to App
            </Link>
          ) : (
            <Link to="/login" className="text-sm text-slate-400 hover:text-white transition-colors">
              Sign In
            </Link>
          )}
        </nav>
      </header>

      {/* Hero */}
      <main>
        <section className="max-w-5xl mx-auto px-6 pt-16 pb-20 text-center">
          <div className="animate-fade-up">
            <h1 className="text-5xl md:text-6xl font-bold mb-4 tracking-tight">
              Read Japanese with
              <span className="text-primary-400"> Confidence</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed">
              Yomitool is a Japanese reading tool with interactive vocabulary lookup, smart tokenization, custom flashcard decks, and Anki export for spaced-repetition study.
            </p>
            <button
              onClick={() => navigate(user ? '/app' : '/signup')}
              className="px-8 py-3.5 bg-primary-600 hover:bg-primary-500 text-white rounded-xl text-base font-semibold transition-colors shadow-lg shadow-primary-600/25"
            >
              {user ? 'Go to App' : 'Get Started Free'}
            </button>
          </div>
        </section>

        {/* Features */}
        <section className="max-w-5xl mx-auto px-6 pb-24">
          <h2 className="text-2xl font-bold text-center mb-10 text-slate-200">
            Everything you need to read Japanese
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((feature, i) => (
              <article
                key={feature.title}
                className="animate-fade-up bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 hover:bg-slate-800/80 transition-colors"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <div className="w-10 h-10 bg-primary-600/20 text-primary-400 rounded-lg flex items-center justify-center mb-4">
                  {feature.icon}
                </div>
                <h3 className="font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{feature.description}</p>
              </article>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section className="max-w-5xl mx-auto px-6 pb-24">
          <h2 className="text-2xl font-bold text-center mb-10 text-slate-200">
            How Yomitool works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-3xl mx-auto">
            {[
              { step: '1', title: 'Paste an article', desc: 'Paste any Japanese text — news, blog posts, novels, or study material.' },
              { step: '2', title: 'Click to learn', desc: 'Click any word for instant dictionary lookup. Save words to custom flashcard decks.' },
              { step: '3', title: 'Review & export', desc: 'Review with built-in flashcards or export to Anki for offline spaced-repetition study.' },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-3 text-lg font-bold">
                  {item.step}
                </div>
                <h3 className="font-semibold text-white mb-1">{item.title}</h3>
                <p className="text-sm text-slate-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-5xl mx-auto px-6 pb-24 text-center">
          <h2 className="text-2xl font-bold text-slate-200 mb-3">Start reading Japanese today</h2>
          <p className="text-slate-400 mb-6">Free to use. No credit card required.</p>
          <button
            onClick={() => navigate(user ? '/app' : '/signup')}
            className="px-8 py-3.5 bg-primary-600 hover:bg-primary-500 text-white rounded-xl text-base font-semibold transition-colors shadow-lg shadow-primary-600/25"
          >
            {user ? 'Go to App' : 'Get Started Free'}
          </button>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-700/50 py-8 text-center text-xs text-slate-500">
        <p>Yomitool — Japanese Reading & Vocabulary Tool</p>
        <p className="mt-1">Built with React, Kuromoji, and Jitendex</p>
      </footer>
    </div>
  );
}
